import React, { useEffect, useState, useRef, useMemo, useCallback } from 'react';
import { useHistory } from 'react-router-dom';
import { useSelector } from 'react-redux';
import TableDroppedList from '@/screens/Inventory/TableDroppedList';
import inventoryStyles from '@/screens/Inventory/styles';
import LoadingV2 from '@/components/Loading';
import { getContainersRedeliveries, getTableList, SaveApiResMessage } from '@/actions';
import { formatDateTimeToUTC } from '@/ctUtlis/formatDate';
import { fDate, fTimeDate, genSize } from '@/actions/Helper';
import SearchContainer from '@/screens/Appointments/SearchContainer';
import Pagination from '@/components/pagination';
import {
  DEFAULT_DROPPED_INVENTORY_TABLE_COLUMNS,
  resolveDroppedTableColumnsFromSettings,
} from '@/screens/Inventory/droppedInventoryTableColumns';

const extractContainersListFromResponse = (response) => {
  const containersPayload =
    response?.data?.containers ?? response?.containers ?? null;

  if (Array.isArray(containersPayload)) {
    return { items: containersPayload, pagination: null };
  }

  if (containersPayload && Array.isArray(containersPayload.data)) {
    return {
      items: containersPayload.data,
      pagination: {
        currentPage: containersPayload.current_page ?? 1,
        lastPage: containersPayload.last_page ?? 1,
        total: containersPayload.total ?? containersPayload.data.length,
      },
    };
  }

  return { items: [], pagination: null };
};

const filterContainersBySearch = (data, query) => {
  if (!query?.trim()) return data;

  return data
    .map((item) => {
      if (item.containers) {
        const filteredContainers = item.containers.filter(
          (container) =>
            container.name &&
            container.name.toLowerCase().includes(query.toLowerCase())
        );

        const cityMatches =
          item.loc && item.loc.toLowerCase().includes(query.toLowerCase());

        if (filteredContainers.length > 0 || cityMatches) {
          return {
            ...item,
            containers:
              filteredContainers.length > 0
                ? filteredContainers
                : item.containers,
          };
        }
        return null;
      }
      if (item.name) {
        return item.name.toLowerCase().includes(query.toLowerCase())
          ? item
          : null;
      }
      return item;
    })
    .filter((item) => item !== null);
};

const mapRedeliveryContainerToTableRow = (container) => {
  const spec = container?.specification || container?.specifications || {};

  return {
    id: container?.id,
    containers: [container],
    size: genSize(spec),
    ssl: container?.company?.name || container?.shipping_line?.slug || '',
    loc:
      container?.address?.city ||
      container?.address?.address ||
      container?.warehouse?.address ||
      '',
    cabinId: container?.cabin_id,
    available_appointments_count: container?.available_appointments_count,
    customer: container?.customer,
    warehouse: container?.warehouse,
    delivery_type: container?.delivery_type,
    delivery_appointment: container?.delivery_appointment,
  };
};

const parseRedeliveriesInventoryRows = (response) => {
  const { items } = extractContainersListFromResponse(response);
  return items.map(mapRedeliveryContainerToTableRow);
};

const buildPaginationFromResponse = (response) => {
  const { pagination: apiPagination } =
    extractContainersListFromResponse(response);

  if (!apiPagination?.lastPage || apiPagination.lastPage <= 1) {
    return { currentPage: 1, pages: [] };
  }

  const pages = [];
  for (let i = 1; i <= apiPagination.lastPage; i++) {
    pages.push(i);
  }

  return {
    currentPage: apiPagination.currentPage || 1,
    pages,
  };
};

const ReportDroppedInventorySection = ({ tableQuery = null }) => {
  const cls = inventoryStyles();
  const history = useHistory();
  const userMetas = useSelector((state) => state.reducer.user?.metas);
  const Sizes = useSelector((state) => state.reducer.size);
  const SSLs = useSelector((state) => state.reducer.ssl);
  const mptyTrackingEnable = userMetas?.enable_empty_tracking?.value ?? 'nis';
  const enable_appointments = userMetas?.enable_appointments?.value ?? '1';

  const [loading, setLoading] = useState(false);
  const [tableTheadTitle, setTableTheadTitle] = useState(
    DEFAULT_DROPPED_INVENTORY_TABLE_COLUMNS
  );
  const [containersData, setContainersData] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [listPage, setListPage] = useState(1);
  const [pageCount, setPageCount] = useState(0);
  const fetchIdRef = useRef(0);

  const pagination = useMemo(() => {
    const pages = [];
    for (let i = 1; i <= pageCount; i++) {
      pages.push(i);
    }
    return { currentPage: listPage, pages };
  }, [listPage, pageCount]);

  const tableQueryKey = useMemo(
    () => (tableQuery ? JSON.stringify(tableQuery) : ''),
    [tableQuery]
  );

  const filteredContainersData = useMemo(
    () => filterContainersBySearch(containersData, searchQuery),
    [containersData, searchQuery]
  );

  useEffect(() => {
    getTableList('inventoryTableDropped')
      .then((res) => {
        const columns = resolveDroppedTableColumnsFromSettings(res);
        if (columns) {
          setTableTheadTitle(columns);
        }
      })
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (!tableQuery?.dateFrom || !tableQuery?.dateTill || !tableQuery?.label) {
      setContainersData([]);
      setLoading(false);
      return;
    }

    const requestId = ++fetchIdRef.current;
    setLoading(true);

    getContainersRedeliveries({
      dateFrom: tableQuery.dateFrom,
      dateTill: tableQuery.dateTill,
      label: tableQuery.label,
      shipping_lines: tableQuery.shipping_lines,
      page: listPage,
    })
      .then((response) => {
        if (fetchIdRef.current !== requestId) return;

        const rows = parseRedeliveriesInventoryRows(response);
        const nextPagination = buildPaginationFromResponse(response);
        setContainersData(rows);
        setPageCount(nextPagination.pages.length);
        if (nextPagination.currentPage !== listPage) {
          setListPage(nextPagination.currentPage);
        }
      })
      .catch((err) => {
        if (fetchIdRef.current !== requestId) return;

        SaveApiResMessage({
          title: 'Error',
          message: err,
          mode: 'warning',
        });
        setContainersData([]);
      })
      .finally(() => {
        if (fetchIdRef.current === requestId) {
          setLoading(false);
        }
      });
  }, [tableQueryKey, tableQuery, listPage]);

  useEffect(() => {
    setSearchQuery('');
    setListPage(1);
    setPageCount(0);
  }, [tableQueryKey]);

  const noop = () => {};

  const openReturnAppointment = useCallback(
    (item, containerId, gatedIn, gatedOut, container) => {
      sessionStorage.setItem('fromReturnAppointments', 'true');

      const sizeLabel =
        item?.size ||
        (container?.specification?.size_type
          ? `${container.specification.size_type}`
          : '');
      const sslLabel = item?.ssl || container?.company?.name;

      const sizeValue = Sizes?.find(({ label }) => label === sizeLabel)?.value;
      const sslValue = SSLs?.find(({ label }) => label === sslLabel)?.value;
      const cabinId =
        container?.cabinId ?? container?.cabin_id ?? item?.cabinId;

      const queryParams = new URLSearchParams({
        apiData: JSON.stringify({
          size: sizeValue,
          ssl: sslValue,
          containerId,
          cabinId,
        }),
      }).toString();

      history.push(
        `/inventory/emptyDropped/appointments?${queryParams}&cabinId=${cabinId}`
      );

      setTimeout(() => {
        sessionStorage.removeItem('fromReturnAppointments');
      }, 100);
    },
    [Sizes, SSLs, history]
  );

  const showTableLoading = loading && !containersData?.length;
  const hasTableQuery =
    tableQuery?.dateFrom && tableQuery?.dateTill && tableQuery?.label;

  return (
    <div className="overflow-x-auto">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-4">
        <h3 className="text-lg font-averta-bold text-primary-text-secondary m-0">
          Dropped Inventory
        </h3>
        <SearchContainer
          width="264px"
          placeholder="Search Container or City"
          setContainerName={setSearchQuery}
          setPage={() => setListPage(1)}
        />
      </div>
      {!hasTableQuery ? (
        <LoadingV2 />
      ) : showTableLoading ? (
        <LoadingV2 />
      ) : (
        <div className={loading ? 'opacity-60 pointer-events-none' : ''}>
          <TableDroppedList
            key={tableQueryKey}
            cls={cls}
            tableTheadTitle={tableTheadTitle}
            containersData={filteredContainersData}
            currentPage={listPage}
            uniqueIndex={0}
            formatDateTimeToUTC={formatDateTimeToUTC}
            formatDateToUTC={formatDateTimeToUTC}
            formatPrice={(v) => v}
            fDate={fDate}
            fTimeDate={fTimeDate}
            enable_appointments={enable_appointments}
            Appointment={openReturnAppointment}
            hideActionsColumn
            shareLink={{ loading: null, DATA: null }}
            handleGetShareLink={noop}
            Edit={noop}
            DelContainer={noop}
            handleChangeContainerStatus={noop}
            timerinterval={{}}
            getActivContainers={noop}
            showContainersModal={false}
            setShowContainersModal={noop}
            setSelectedRow={noop}
            Del={noop}
            mptyTrackingEnable={mptyTrackingEnable}
            activeTab="dropped"
          />
          <Pagination
            pagination={pagination}
            setPagination={(updater) => {
              const next =
                typeof updater === 'function' ? updater(pagination) : updater;
              setListPage(next.currentPage);
            }}
          />
        </div>
      )}
    </div>
  );
};

export default ReportDroppedInventorySection;
